'use client';

import React from 'react';
import Image from 'next/image';
import { MotionDiv } from '@/components/motion';

export default function AboutCompany() {
  return (
    <section className="mb-16">
      {/* Header - Nuestra Empresa */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-12 border-l-4 border-brand-primary pl-6"
      >
        <h2 className="text-3xl font-bold mb-4 text-gray-900">Nuestra Empresa</h2>
        <p className="text-gray-700 leading-relaxed text-lg max-w-4xl">
          Desde <span className="font-semibold text-brand-primary">1982</span>, Droquimar SRL realiza actividades en la importación, distribución, fabricación y comercialización de productos químicos y productos de limpieza.
        </p>
        <p className="text-gray-600 leading-relaxed mt-4 max-w-4xl">
          La infraestructura de la empresa comprende dos depósitos ubicados en la localidad de La Tablada, Provincia de Buenos Aires, con un total de <span className="font-semibold">2400 metros cuadrados cubiertos</span>, y una logística de distribución propia para entrega de productos químicos en cualquier lugar de Capital Federal y Gran Buenos Aires y para envíos a cualquier parte del país.
        </p>
      </MotionDiv>

      {/* Image and Mission/Vision */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <MotionDiv
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative"
        >
          {/* Decorative elements */}
          <div className="absolute -top-6 -left-6 w-24 h-24 bg-brand-primary/10 rounded-full blur-2xl"></div>
          <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl"></div>

          {/* Main image container with fancy border */}
          <div className="relative group">
            {/* Animated border effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-brand-primary via-blue-500 to-purple-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>

            {/* Image container */}
            <div className="relative h-[450px] rounded-2xl overflow-hidden shadow-2xl">
              <Image
                className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105"
                fill
                alt="Acerca de Droquimar"
                src="/images/company/image-101.webp"
                priority
                sizes="(max-width: 768px) 100vw, 50vw"
              />

              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl p-4 border-2 border-brand-primary/20">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-primary">43+</div>
                <div className="text-sm text-gray-600 font-medium">Años de</div>
                <div className="text-sm text-gray-600 font-medium">Experiencia</div>
              </div>
            </div>
          </div>
        </MotionDiv>

        <MotionDiv
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="space-y-8"
        >
          <div className="border-l-4 border-blue-500 pl-6 bg-blue-50/50 py-4 rounded-r-lg">
            <h3 className="text-xl font-semibold mb-3 text-blue-900">Misión</h3>
            <p className="text-gray-700 leading-relaxed">
              Importar, fraccionar y comercializar productos químicos, como así también, fabricar productos de limpieza y de mantenimiento industrial con el mayor compromiso con la calidad, entrega y eficiencia, promoviendo la mejora continua y la gestión sustentable en todos los procesos.
            </p>
          </div>

          <div className="border-l-4 border-purple-500 pl-6 bg-purple-50/50 py-4 rounded-r-lg">
            <h3 className="text-xl font-semibold mb-3 text-purple-900">Visión</h3>
            <p className="text-gray-700 leading-relaxed">
              Ser referentes para el sector químico, aportando la mejor calidad y eficiente gama de insumos y productos para los procesos de producción; siempre promoviendo el compromiso con el entorno y la utilización sustentable de los recursos.
            </p>
          </div>
        </MotionDiv>
      </div>

      <div className="mt-20">
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-10 border-l-4 border-brand-primary pl-6"
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-900">Nuestros Valores</h2>
          <p className="text-gray-700 leading-relaxed text-lg max-w-4xl">
            Promovemos la gestión sustentable a través de todos nuestros procesos, haciendo principal hincapié en el orden, la limpieza, cuidado y compromiso. Fomentamos la capacitación continua para todos los que son parte de nuestro equipo de trabajo y asumimos con iniciativa la concientización sobre el cuidado del medio ambiente.
          </p>
        </MotionDiv>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <MotionDiv
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="group h-full"
          >
            <div className="h-full bg-white border-l-4 border-blue-500 rounded-r-xl shadow-lg hover:shadow-2xl p-6 transition-all duration-300 hover:-translate-y-1 flex flex-col">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all duration-300">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-blue-900 group-hover:text-blue-600 transition-colors duration-300">Calidad y Eficiencia</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Nos comprometemos a ofrecer productos de la más alta calidad, garantizando la satisfacción de nuestros clientes. La celeridad y eficiencia son pilares fundamentales en nuestros procesos.
              </p>
            </div>
          </MotionDiv>

          <MotionDiv
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="group h-full"
          >
            <div className="h-full bg-white border-l-4 border-green-500 rounded-r-xl shadow-lg hover:shadow-2xl p-6 transition-all duration-300 hover:-translate-y-1 flex flex-col">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all duration-300">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-green-900 group-hover:text-green-600 transition-colors duration-300">Compromiso y Confianza</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Entendemos que el respeto constituye el pilar central en la relación comercial con proveedores y clientes. Nuestro compromiso es con la calidad, entrega y la atención personalizada, construyendo relaciones de confianza duraderas.
              </p>
            </div>
          </MotionDiv>

          <MotionDiv
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="group h-full"
          >
            <div className="h-full bg-white border-l-4 border-purple-500 rounded-r-xl shadow-lg hover:shadow-2xl p-6 transition-all duration-300 hover:-translate-y-1 flex flex-col">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all duration-300">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-purple-900 group-hover:text-purple-600 transition-colors duration-300">Sustentabilidad</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Trabajamos constantemente para minimizar nuestro impacto ambiental, promoviendo prácticas sostenibles en todas nuestras operaciones. Asumimos la concientización a todos nuestros públicos de interés y en especial a las nuevas generaciones sobre las buenas prácticas ambientales.
              </p>
            </div>
          </MotionDiv>
        </div>
      </div>
    </section>
  );
} 